/**
 * Display formatting helpers.
 *
 * Local, dependency-free formatting for money, per-bushel prices, and UTC
 * timestamps. Money is shown to 2 dp with negative values carrying a leading
 * sign (Req 5.5); per-bushel breakeven is shown to 4 dp (Req 3.4); price
 * timestamps are rendered explicitly in UTC (Req 4.8, 9.2).
 */

/** Format a dollar amount to 2 dp with a `$` prefix (negatives keep the sign). */
export function formatUsd(value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  const fixed = value.toFixed(2);
  return fixed.startsWith("-") ? `-$${fixed.slice(1)}` : `$${fixed}`;
}

/** Format a per-bushel price to 4 dp with a `$` prefix (Req 3.4). */
export function formatPerBushel4(value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  return `$${value.toFixed(4)}`;
}

/** Format a cash bid / per-bushel dollar value to 2 dp (Req 4). */
export function formatPrice2(value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  return `$${value.toFixed(2)}`;
}

/**
 * Render an ISO-8601 instant explicitly as UTC (e.g. `2024-05-01 14:30 UTC`),
 * so the displayed cash-bid timestamp is unambiguous (Req 4.8, 9.2). Returns
 * the raw string if it cannot be parsed.
 */
export function formatUtc(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  const pad = (n: number): string => String(n).padStart(2, "0");
  const y = date.getUTCFullYear();
  const mo = pad(date.getUTCMonth() + 1);
  const d = pad(date.getUTCDate());
  const h = pad(date.getUTCHours());
  const mi = pad(date.getUTCMinutes());
  return `${y}-${mo}-${d} ${h}:${mi} UTC`;
}

/** Human label for a retrieval outcome. */
export function outcomeLabel(
  outcome: "success" | "unavailable" | "manual_override"
): string {
  switch (outcome) {
    case "success":
      return "Success";
    case "unavailable":
      return "Unavailable";
    case "manual_override":
      return "Manual override";
    default:
      return outcome;
  }
}

/** Human label for a price source. */
export function sourceLabel(source: "acoop" | "manual" | "usda_ams"): string {
  switch (source) {
    case "acoop":
      return "Assumption Co-op";
    case "manual":
      return "Manual entry";
    case "usda_ams":
      return "USDA AMS";
    default:
      return source;
  }
}

/** Turn a camelCase input name into a readable label (e.g. `cashBid` → `Cash bid`). */
export function humanizeField(field: string): string {
  const spaced = field.replace(/([A-Z])/g, " $1").toLowerCase().trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
